import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Form from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import {FormattedMessage} from 'react-intl';
import MessageDefinitions from './MessageDefinitions';
import {textInput} from './FormInput';
import LocationsMap from './LocationMap';
import {pruneObj, Screens} from './constants';
import {LocationShape} from '@ignitesales/neuro-application/src/shapes';
import getLogger from '@ignitesales/logger-es';

const logger = getLogger({component: 'ContactForm'});

const AppointmentLocationForm = props => {
  const {
    fields = AppointmentLocationForm.defaultFields,
    schema = AppointmentLocationForm.defaultSchema,
    selectedLocation,
    locationSearchForm,
    setLocation,
    changeScreen,
    onLocationSearchChange,
    getLocations,
    availableLocations = [],
    isProcessing
  } = props;

  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [searchValue, setSearchValue] = useState(locationSearchForm || { zipCode: ''});
  const formRef = useRef(null);
  const formValid = !errors.length;

  useEffect(() => {
    validateForm(searchValue);
  }, []);

  const validateForm = state => {
    const f = {...state};
    let e = [];
    const prunedState = pruneObj(f);
    if (formRef.current && formRef.current.validate) {
      formRef.current.validate(prunedState).then((formErrors = []) => {
        e = formErrors.map(error => {
          return {
            key: error.path,
            errors: [error.message]
          };
        });

        setErrors(e);
      });
    }

    setSearchValue(state);
    if(!e.length){
      onLocationSearchChange(state);
    }
  };

  const formSubmitted = () => {
    if (selectedLocation.id) {
      changeScreen(Screens.APPT_CALENDAR);
    }
  };

  const formFields = fields.map(field => {
    let type = 'text';
    return textInput({
      type,
      name: field,
      labelMessage: MessageDefinitions[field],
      placeholderMessage: MessageDefinitions[`${field}Placeholder`],
      errorMessage: MessageDefinitions[`${field}Error`],
      hasError: searchValue.zipCode && errors.find(e => e.key === field)
    });
  });

  const buttonMessage =
    isProcessing
      ? <FormattedMessage{...MessageDefinitions.loading} />
      : <FormattedMessage{...MessageDefinitions.continue} />;

  const selectedLocationString =
    selectedLocation && selectedLocation.id
      ?
      <div className="selectedLocationString">Selected Location:
        <span className="selectedLocationString__name">{selectedLocation.name} </span>
        <span className="selectedLocationString__address">{selectedLocation.address}, </span>
        <span className="selectedLocationString__city">{selectedLocation.city}, {selectedLocation.state} {selectedLocation.postalCode}</span>
      </div>
      : null;

  const errorString =
    !formValid && showErrors
      ? <div className="contactMe__error"><FormattedMessage {...MessageDefinitions.appointmentLocationError} /></div>
      : null;

  const loadingOverlay =
    isProcessing
      ? <div className="contactMe__loadingOverlay" />
      : null;

  return (
    <Validate schema={schema} ref={formRef}>
      <Form value={searchValue} className="contactMe__form" onChange={validateForm} onSubmit={formSubmitted}>
        <div className="form-body">
          {loadingOverlay}
          <h5 className="form-description"><FormattedMessage {...MessageDefinitions.appointmentLocationFormMessage} /></h5>
          {selectedLocationString}
          {formFields}
          <LocationsMap
            zipCode={searchValue.zipCode}
            onClick={setLocation}
            selectedLocation={selectedLocation}
            onGeoLocationChange={getLocations}
            onGeoLocationFailure={(ex) => {logger.error('Failed to get geo location coordinates', ex);}}
            locations={availableLocations}
            googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyA-tE9qr3vCaZsrv1KveSqhVOT_d9zZ39A&v=3.exp&libraries=geometry,drawing,places"
            loadingElement={<div className='locationMap__loading'/>}
            containerElement={<div className='locationMap'/>}
            mapElement={<div className='locationMap__map'/>}
          />
          {errorString}
        </div>
        <div className="form-actions" onClick={() => setShowErrors(true)}>
          <button type="submit" className="form-submitBtn" disabled={!formValid || isProcessing}>
            {buttonMessage}
          </button>
        </div>
      </Form>
    </Validate>
  );
};

AppointmentLocationForm.propTypes = {
  fields: PropTypes.array,
  schema: PropTypes.object,
  selectedScreen: PropTypes.string,
  searchFormValue: PropTypes.string,
  selectedLocation: LocationShape,
  setLocation: PropTypes.func,
  onScreenChange: PropTypes.func,
  changeZip: PropTypes.func
};

AppointmentLocationForm.defaultSchema = {
  type: 'object',
  properties: {
    zipCode: {
      type: 'string',
      minLength: 5,
      'pattern': '^[0-9]{5}(?:-[0-9]{4})?$'
    }
  },
  required: ['zipCode']
};

AppointmentLocationForm.defaultFields = ['zipCode'];

export default AppointmentLocationForm;
